import React, { useEffect, useState } from 'react'

import DOMPurify from 'dompurify'

import ProviderService from '../../../services/ProviderService'
import validation from '../../../validation'
import AddButton from '../../controls/AddButton'
import CheckBox from '../../controls/CheckBox'
import DatePicker from '../../controls/DatePicker'
import RemoveButton from '../../controls/RemoveButton'
import TextArea from '../../controls/TextArea'
import CustomDropDown from '../../CustomDropDown'

export default {
  name: 'Surgeries/Hospitalizations',
  group: 'Case Details',
  validationSchema: getValidationSchema(),
  Component,
}

// shamelessly using function hoisting for code clarity

function getValidationSchema() {
  return validation.object({
    surgeriesAndHospitalizations: validation
      .arrayOf(
        validation.object({
          title: validation.name('Procedure/Surgery').required(),
          dateAdmitted: validation.date('Date Admitted'),
          dateOfSurgery: validation.date('Date of Surgery'),
          dateDischarged: validation.date('Date Discharged'),
          provider: validation.shortText('Physician/Specialist'),
          results: validation.richText('Results'),
          // reason: validation.shortText('Reason')
        }),
      )
      .nullable(),
  })
}

function Component({
  addItemToArray,
  removeItemFromArray,
  createInputProps,
  getValue,
  ErrorMessage,
  caseModel,
  caseNumber,
}) {
  const [editingIndex, setEditingIndex] = useState(null)
  const surgeriesAndHospitalizations = getValue('surgeriesAndHospitalizations', [])
  const [caseProviders, setCaseProviders] = useState([])

  const cId = caseNumber || caseModel?.caseNumber || caseModel?.CaseNumber || caseModel?.id || caseModel?.Id

  useEffect(() => {
    if (cId && cId !== 'N/A') {
      ProviderService.getProvidersForCase(cId)
        .then(allProviders => {
          const treatmentOnly = (allProviders || []).filter(
            p =>
              !p.type ||
              (p.type && !p.type.toLowerCase().includes('pharmacy') && !p.type.toLowerCase().includes('equipment')),
          )
          setCaseProviders(treatmentOnly)
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.warn(err)
        })
    }
  }, [cId])

  const handleAddProcedure = () => {
    const nextIndex = surgeriesAndHospitalizations.length
    addItemToArray('surgeriesAndHospitalizations', {})
    setEditingIndex(nextIndex)
  }

  const handleRemove = index => {
    removeItemFromArray('surgeriesAndHospitalizations', index)
    if (editingIndex === index) {
      setEditingIndex(null)
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1)
    }
  }

  return (
    <div>
      <div className="SurgeriesAndHospitalizations-component">
        <h4 className="pb-3">Surgeries/Hospitalizations</h4>
        {surgeriesAndHospitalizations.map((x, index) => {
          const namePrefix = `surgeriesAndHospitalizations[${index}]`
          const isEditing = editingIndex === index
          return (
            <React.Fragment key={namePrefix}>
              <Procedure
                namePrefix={namePrefix}
                createInputProps={createInputProps}
                getValue={getValue}
                ErrorMessage={ErrorMessage}
                caseProviders={caseProviders}
                isEditing={isEditing}
                onEdit={() => setEditingIndex(index)}
                onDone={() => setEditingIndex(null)}
                handleRemove={() => handleRemove(index)}
              />
              <hr />
            </React.Fragment>
          )
        })}
        <AddButton id="addProcedureButton" onClick={handleAddProcedure}>
          Add Surgery/Hospitalization
        </AddButton>
      </div>
    </div>
  )
}

function Procedure({
  namePrefix,
  createInputProps,
  ErrorMessage,
  handleRemove,
  caseProviders = [],
  getValue,
  isEditing,
  onEdit,
  onDone,
}) {
  const providerVal = getValue(`${namePrefix}.provider`)
  const title = getValue(`${namePrefix}.title`, '')
  const dateAdmitted = getValue(`${namePrefix}.dateAdmitted`, '')
  const dateOfSurgery = getValue(`${namePrefix}.dateOfSurgery`, '')
  const dateDischarged = getValue(`${namePrefix}.dateDischarged`, '')
  const results = getValue(`${namePrefix}.results`, '')
  const relatedToInjury = getValue(`${namePrefix}.relatedToInjury`)

  const providerOptions = caseProviders.map(p => {
    const displayName =
      p.classification === 'Individual'
        ? `${p.firstName || ''} ${p.lastName || ''}${p.suffix ? `, ${p.suffix}` : ''}`.trim()
        : p.name || p.organizationName || ''
    return {
      id: displayName,
      name: displayName,
    }
  })

  // If providerVal is a GUID matching a provider, ensure it resolves to that provider's display name
  if (providerVal) {
    const matchingProvider = caseProviders.find(p => p.id === providerVal)
    if (matchingProvider) {
      const displayName =
        matchingProvider.classification === 'Individual'
          ? `${matchingProvider.firstName || ''} ${matchingProvider.lastName || ''}${
              matchingProvider.suffix ? `, ${matchingProvider.suffix}` : ''
            }`.trim()
          : matchingProvider.name || matchingProvider.organizationName || ''
      if (!providerOptions.some(o => o.id === providerVal)) {
        providerOptions.push({ id: providerVal, name: displayName })
      }
    }
  }

  const selectedProvider = providerOptions.find(o => o.id === providerVal || o.name === providerVal)
  const providerDisplay = selectedProvider ? selectedProvider.name : providerVal
  const hasResults = Boolean(results && results.replace(/<[^>]*>/g, '').trim())

  if (!isEditing) {
    return (
      <div className="d-flex align-items-start justify-content-between clearfix">
        <div className="w-100 mr-3">
          <div>
            <span className="font-weight-semibold">{title || <em className="text-muted">Untitled Procedure</em>}</span>
            {providerDisplay && (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                {providerDisplay}
              </span>
            )}
            {dateOfSurgery ? (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                Surgery: {dateOfSurgery}
              </span>
            ) : dateAdmitted || dateDischarged ? (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                {[dateAdmitted && `Admitted: ${dateAdmitted}`, dateDischarged && `Discharged: ${dateDischarged}`]
                  .filter(Boolean)
                  .join(' • ')}
              </span>
            ) : null}
            {relatedToInjury && <span className="badge badge-info ml-3">Related to Injury</span>}
          </div>
          {hasResults && (
            <div className="text-muted mt-1" style={{ fontSize: '0.85em' }}>
              <span>Results: </span>
              <div className="d-inline" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(results) }} />
            </div>
          )}
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={onEdit}
            title="Edit this surgery/hospitalization"
          >
            <i className="fas fa-pen" aria-hidden="true" />
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    )
  }

  return (
    <div className="clearfix">
      <div className="form-group">
        <label className="form-label" htmlFor={`${namePrefix}.title`}>
          Reason for Admission
        </label>
        <input
          type="text"
          className="form-control"
          maxLength={validation.maxLengths.name}
          {...createInputProps(`${namePrefix}.title`)}
        />
        <ErrorMessage name={`${namePrefix}.title`} />
      </div>
      <div className="form-row">
        <div className="form-group col-md-4">
          <label className="form-label" htmlFor={`${namePrefix}.dateAdmitted`}>
            Date Admitted
          </label>
          <DatePicker {...createInputProps(`${namePrefix}.dateAdmitted`)} />
        </div>
        <div className="form-group col-md-4">
          <label className="form-label" htmlFor={`${namePrefix}.dateOfSurgery`}>
            Date of Surgery
          </label>
          <DatePicker {...createInputProps(`${namePrefix}.dateOfSurgery`)} />
        </div>
        <div className="form-group col-md-4">
          <label className="form-label" htmlFor={`${namePrefix}.dateDischarged`}>
            Date Discharged
          </label>
          <DatePicker {...createInputProps(`${namePrefix}.dateDischarged`)} />
        </div>
      </div>
      <div>
        <ErrorMessage name={`${namePrefix}.dateAdmitted`} />
        <ErrorMessage name={`${namePrefix}.dateOfSurgery`} />
        <ErrorMessage name={`${namePrefix}.dateDischarged`} />
      </div>
      <div className="form-row">
        <div className="form-group col-md-6">
          <label className="form-label" htmlFor={`${namePrefix}.provider`}>
            Physician/Specialist
          </label>
          <CustomDropDown
            id={`${namePrefix}.provider`}
            items={providerOptions}
            placeholderText="Search Providers"
            isClearable
            {...createInputProps(`${namePrefix}.provider`)}
          />
          <ErrorMessage name={`${namePrefix}.provider`} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group col-md-12">
          <label className="form-label" htmlFor={`${namePrefix}.results`}>
            Results
          </label>
          <TextArea
            areaProps={{ ...createInputProps(`${namePrefix}.results`) }}
            rows={3}
            maxLength={validation.maxLengths.richText}
          />
          <ErrorMessage name={`${namePrefix}.results`} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group mt-2 col-4 col-sm-6 col-md-3 col-lg-4">
          <CheckBox {...createInputProps(`${namePrefix}.relatedToInjury`)}>Related to Injury</CheckBox>
        </div>
      </div>
      <div className="form-row mb-2 mt-2">
        <div className="col-12 d-flex align-items-end justify-content-end pb-1" style={{ gap: '8px' }}>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onDone}>
            Done
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    </div>
  )
}
